import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import Table from "../../../core/common/dataTable/index"
import { getAllSystems } from "../../../service/systems"
import { getAllClasses } from "../../../service/classes"
import {
  listGroups,
  createGroup,
  updateGroup,
  deleteGroup,
  type Group,
} from "../../../service/group"

type SystemOpt = { _id: string; name: string; code?: string }
type ClassOpt = { _id: string; name: string; system: string; order?: number }

// Fixed display/selection priority: Matric, O-Level, AKU, College.
const SYSTEM_PRIORITY: Record<string, number> = { MATRIC: 1, OLEVEL: 2, AKU: 3, COLLEGE: 4 }
const byPriority = (systems: SystemOpt[]) =>
  [...systems].sort((a, b) => (SYSTEM_PRIORITY[a.code || ""] ?? 99) - (SYSTEM_PRIORITY[b.code || ""] ?? 99))

const AcademicGroups = () => {
  const [items, setItems] = useState<Group[]>([])
  const [systems, setSystems] = useState<SystemOpt[]>([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<Group | "new" | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const fetchItems = async () => {
    setLoading(true)
    try {
      const res = await listGroups()
      setItems(res?.data || [])
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load groups")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchItems()
    getAllSystems()
      .then((r: any) => setSystems(byPriority(r?.data || r?.systems || r || [])))
      .catch(() => setSystems([]))
  }, [])

  useEffect(() => { setPage(1) }, [items.length])

  const handleDelete = async () => {
    if (!deleteId) return
    try {
      await deleteGroup(deleteId)
      toast.success("Deleted")
      setDeleteId(null)
      fetchItems()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Delete failed")
    }
  }

  const sysName = (s: Group["system"]) => (typeof s === "string" ? "" : s?.name) || ""
  const classNames = (classes: Group["classes"]) =>
    classes.map((c) => (typeof c === "string" ? c : c?.name)).filter(Boolean).join(", ")

  const columns = [
    { title: "Name", dataIndex: "name" },
    { title: "System", dataIndex: "system", render: (s: any) => sysName(s) || "—" },
    { title: "Classes", dataIndex: "classes", render: (c: any) => classNames(c || []) || "—" },
    {
      title: "Status",
      dataIndex: "status",
      render: (v: string) =>
        v === "inactive"
          ? <span className="badge bg-secondary-transparent text-secondary">Inactive</span>
          : <span className="badge bg-success-transparent text-success">Active</span>,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, r: Group) => (
        <div className="d-flex gap-2">
          <Link to="#" className="btn btn-sm btn-outline-primary" onClick={() => setEditing(r)}><i className="ti ti-edit" /></Link>
          <Link to="#" className="btn btn-sm btn-outline-danger" onClick={() => setDeleteId(r._id)}><i className="ti ti-trash" /></Link>
        </div>
      ),
    },
  ]

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Academic Groups</h3>
            <p className="text-muted mb-0">Streams like Computer/Biology or Pre-Medical/Commerce, scoped to a System and a specific set of Classes.</p>
          </div>
          <button className="btn btn-primary" onClick={() => setEditing("new")}>
            <i className="ti ti-square-rounded-plus me-2" />New Group
          </button>
        </div>

        <div className="card">
          <div className="card-body p-0 py-3">
            <Table
              dataSource={items}
              columns={columns}
              loading={loading}
              pagination={{ currentPage: page, limit: pageSize, total: items.length, onChange: (p: number, ps: number) => { setPage(p); setPageSize(ps) } }}
            />
          </div>
        </div>
      </div>

      {editing && (
        <GroupModal
          editing={editing}
          systems={systems}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchItems() }}
        />
      )}

      {deleteId && (
        <>
          <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body text-center p-4">
                  <span className="text-danger" style={{ fontSize: 40 }}><i className="ti ti-trash-x" /></span>
                  <h4 className="mt-2">Delete Group</h4>
                  <p className="text-muted">Admissions/enrollments already assigned to it keep their saved group. Continue?</p>
                  <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-light" onClick={() => setDeleteId(null)}>Cancel</button>
                    <button className="btn btn-danger" onClick={handleDelete}>Yes, Delete</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  )
}

const GroupModal = ({
  editing,
  systems,
  onClose,
  onSaved,
}: {
  editing: Group | "new"
  systems: SystemOpt[]
  onClose: () => void
  onSaved: () => void
}) => {
  const isEdit = editing !== "new"
  const [name, setName] = useState(isEdit ? editing.name : "")
  const [system, setSystem] = useState(
    isEdit ? (typeof editing.system === "string" ? editing.system : editing.system?._id) : ""
  )
  const [status, setStatus] = useState<"active" | "inactive">(isEdit ? editing.status : "active")
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>(
    isEdit ? editing.classes.map((c) => (typeof c === "string" ? c : c._id)) : []
  )
  const [classOptions, setClassOptions] = useState<ClassOpt[]>([])
  const [loadingClasses, setLoadingClasses] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!system) { setClassOptions([]); return }
    setLoadingClasses(true)
    // all=true: a Group can apply campus-wide (any campus offering this
    // System), so show every class under the System, not just the
    // currently-active campus's subset.
    getAllClasses({ system, limit: 200, all: "true" })
      .then((r) => setClassOptions((r.classes || []) as any))
      .catch(() => setClassOptions([]))
      .finally(() => setLoadingClasses(false))
  }, [system])

  const toggleClass = (id: string) =>
    setSelectedClassIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]))

  const sortedClassOptions = useMemo(
    () => [...classOptions].sort((a, b) => (a.order ?? 999) - (b.order ?? 999)),
    [classOptions]
  )

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) { setError("Name is required"); return }
    if (!system) { setError("Pick a system"); return }
    if (!selectedClassIds.length) { setError("Select at least one class"); return }
    setSaving(true); setError(null)
    try {
      if (isEdit) {
        await updateGroup(editing._id, { name: name.trim(), classes: selectedClassIds, status })
        toast.success("Updated")
      } else {
        await createGroup({ name: name.trim(), system, classes: selectedClassIds })
        toast.success("Created")
      }
      onSaved()
    } catch (e2: any) {
      setError(e2?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">{isEdit ? "Edit group" : "New group"}</h4>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <form onSubmit={submit}>
              <div className="modal-body">
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Name</label>
                    <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Computer, Pre-Medical" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">System</label>
                    <select className="form-select" value={system} disabled={isEdit} onChange={(e) => { setSystem(e.target.value); setSelectedClassIds([]) }}>
                      <option value="">— Select system —</option>
                      {systems.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                    </select>
                  </div>
                  {isEdit && (
                    <div className="col-md-6">
                      <label className="form-label">Status</label>
                      <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value as "active" | "inactive")}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  )}
                </div>

                <label className="form-label">Applies to which classes?</label>
                {!system ? (
                  <p className="text-muted small">Pick a system first.</p>
                ) : loadingClasses ? (
                  <p className="text-muted small">Loading classes…</p>
                ) : sortedClassOptions.length === 0 ? (
                  <p className="text-muted small">This system has no classes yet.</p>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {sortedClassOptions.map((c) => (
                      <label key={c._id} className="form-check form-check-inline border rounded px-2 py-1">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedClassIds.includes(c._id)}
                          onChange={() => toggleClass(c._id)}
                        />
                        <span className="form-check-label">{c.name}</span>
                      </label>
                    ))}
                  </div>
                )}

                {error && <div className="alert alert-danger mt-3 mb-0 py-2 px-3 small">{error}</div>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={onClose} disabled={saving}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving…" : isEdit ? "Update" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

export default AcademicGroups
